'use client';

// ImagePreview - Display uploaded images with processing status and extracted content

import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { useTranslations } from 'next-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';

export type ImageStatus = 'pending' | 'uploading' | 'processing' | 'ready' | 'error';

export interface UploadedImage {
  /** Unique identifier for the image (local tracking ID) */
  id: string;
  /** Database content ID (for deletion sync) */
  contentId?: string;
  /** Original file name */
  fileName: string;
  /** File size in bytes */
  fileSize: number;
  /** MIME type of the image */
  mimeType: string;
  /** Current processing status */
  status: ImageStatus;
  /** Object URL for local preview (created via URL.createObjectURL) */
  previewUrl?: string;
  /** URL from Supabase Storage after upload */
  thumbnailUrl?: string;
  /** Extracted text from OCR */
  extractedText?: string;
  /** AI-generated description */
  description?: string;
  /** Combined preview (first 500 chars of extracted content) */
  preview?: string;
  /** Error message if processing failed */
  errorMessage?: string;
  /** Upload/processing progress (0-100) */
  progress?: number;
}

export interface ImagePreviewProps {
  /** The uploaded image data */
  image: UploadedImage;
  /** Callback to remove the image */
  onRemove: (imageId: string) => void;
  /** Callback to retry failed processing */
  onRetry?: (imageId: string) => void;
  /** Whether actions are disabled */
  disabled?: boolean;
}

/**
 * Formats file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * ImagePreview - Displays an uploaded image with thumbnail, status, and extracted content
 *
 * Features:
 * - Thumbnail preview (120x120px max)
 * - File name and size display
 * - Progress indicator during upload/processing
 * - Extracted content preview with tooltip for full content
 * - Remove and retry buttons
 * - Error state display
 * - RTL support via CSS logical properties
 *
 * @example
 * ```tsx
 * <ImagePreview
 *   image={uploadedImage}
 *   onRemove={(id) => removeImage(id)}
 *   onRetry={(id) => retryProcessing(id)}
 * />
 * ```
 */
export function ImagePreview({
  image,
  onRemove,
  onRetry,
  disabled = false,
}: ImagePreviewProps) {
  const t = useTranslations('creator.imageUpload');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  // Sync local state with previewUrl prop
  // Object URL lifecycle is managed by the parent (useImageUpload hook):
  // - Created in onMutate when upload starts
  // - Revoked in removeImage() and clearImages() when image is removed
  // This ensures URLs are properly cleaned up even if component unmounts unexpectedly
  useEffect(() => {
    if (image.previewUrl && image.previewUrl.startsWith('blob:')) {
      setObjectUrl(image.previewUrl);
    }
  }, [image.previewUrl]);

  const thumbnailSrc = image.thumbnailUrl || objectUrl || image.previewUrl;
  const isProcessing = image.status === 'uploading' || image.status === 'processing';
  const canRemove = !disabled && !isProcessing;
  const canRetry = !disabled && image.status === 'error' && onRetry;

  // Status text
  const getStatusText = () => {
    switch (image.status) {
      case 'pending':
        return t('preview.processing');
      case 'uploading':
        return t('preview.processing');
      case 'processing':
        return t('preview.processing');
      case 'ready':
        return t('preview.processed');
      case 'error':
        return t('preview.failed');
      default:
        return '';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: image.status === 'error' ? 'error.light' : 'divider',
        borderRadius: 1,
        backgroundColor: image.status === 'error' ? 'error.lighter' : 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {/* Thumbnail */}
        <Box
          sx={{
            flexShrink: 0,
            width: 80,
            height: 80,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {thumbnailSrc ? (
            <Box
              component="img"
              src={thumbnailSrc}
              alt={image.fileName}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <ImageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          )}
        </Box>

        {/* Image info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* File name and size */}
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {image.fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(image.fileSize)}
          </Typography>

          {/* Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {image.status === 'ready' && (
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
            )}
            {image.status === 'error' && (
              <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />
            )}
            <Typography
              variant="caption"
              color={image.status === 'error' ? 'error.main' : 'text.secondary'}
            >
              {getStatusText()}
            </Typography>
          </Box>

          {/* Progress bar */}
          {isProcessing && (
            <Box sx={{ mt: 1 }}>
              {image.progress !== undefined ? (
                <LinearProgress
                  variant="determinate"
                  value={image.progress}
                  sx={{ borderRadius: 1, height: 4 }}
                />
              ) : (
                <LinearProgress
                  variant="indeterminate"
                  sx={{ borderRadius: 1, height: 4 }}
                />
              )}
            </Box>
          )}

          {/* Error message */}
          {image.status === 'error' && image.errorMessage && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {t(`error.${image.errorMessage}`)}
            </Typography>
          )}

          {/* Extracted content preview */}
          {image.status === 'ready' && image.preview && (
            <Tooltip title={image.preview} arrow placement="bottom-start">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  backgroundColor: 'action.hover',
                  p: 0.75,
                  borderRadius: 0.5,
                  cursor: 'help',
                }}
              >
                {image.preview}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
          {/* Remove button */}
          <IconButton
            size="small"
            onClick={() => onRemove(image.id)}
            disabled={!canRemove}
            aria-label={t('preview.remove')}
            sx={{
              '&:hover': {
                color: 'error.main',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>

          {/* Retry button */}
          {canRetry && (
            <IconButton
              size="small"
              onClick={() => onRetry(image.id)}
              aria-label={t('preview.retry')}
              sx={{
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export interface ImagePreviewListProps {
  /** List of uploaded images */
  images: UploadedImage[];
  /** Callback to remove an image */
  onRemove: (imageId: string) => void;
  /** Callback to retry failed processing */
  onRetry?: (imageId: string) => void;
  /** Whether actions are disabled */
  disabled?: boolean;
}

/**
 * ImagePreviewList - Displays a list of uploaded images with their status
 *
 * @example
 * ```tsx
 * <ImagePreviewList
 *   images={uploadedImages}
 *   onRemove={(id) => removeImage(id)}
 *   onRetry={(id) => retryProcessing(id)}
 * />
 * ```
 */
export function ImagePreviewList({
  images,
  onRemove,
  onRetry,
  disabled = false,
}: ImagePreviewListProps) {
  if (images.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: 2,
      }}
    >
      {images.map((image) => (
        <ImagePreview
          key={image.id}
          image={image}
          onRemove={onRemove}
          {...(onRetry && { onRetry })}
          disabled={disabled}
        />
      ))}
    </Box>
  );
}
